import SchemaOrg from '@/components/base/SchemaOrg';
import GuideLayout from '@/pages/guide/components/GuideLayout';

const shippingSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Superbuy Sheet Shipping Guide: International Delivery & Order Tips',
  description: 'Everything you need to know about Superbuy Sheet product shipping, delivery timelines, order tracking, and tips for international shoppers. Updated 2026 guide.',
  image: 'https://readdy.ai/api/search-image?query=Clean%20modern%20photography%20of%20cardboard%20shipping%20boxes%20neatly%20stacked%20with%20a%20laptop%20showing%20delivery%20tracking%20interface%2C%20warm%20cream%20background%2C%20soft%20natural%20light%2C%20sage%20green%20packaging%20tape%20accents%2C%20minimalist%20ecommerce%20logistics%20aesthetic%2C%20organized%20and%20calm%20atmosphere%2C%20high%20detail%20texture&width=1600&height=700&seq=guide-shipping-p01&orientation=landscape',
  author: { '@type': 'Person', name: 'Alex Carter', jobTitle: 'Shopping Research Analyst' },
  datePublished: '2026-07-01',
  dateModified: '2026-07-13',
  publisher: { '@type': 'Organization', name: 'Superbuy Sheet', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png' } },
  mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://superbuysheet.click/guide/shipping' },
};

export default function ShippingGuide() {
  return (
    <>
      <SchemaOrg data={shippingSchema} />
      <GuideLayout
        title="Superbuy Sheet Shipping Guide: International Delivery & Order Tips"
        description="Everything you need to know about Superbuy Sheet product shipping, delivery timelines, order tracking, and tips for international shoppers. Updated 2026 guide."
        breadcrumbLabel="Shipping Guide"
        readTime="7 min read"
        imageUrl="https://readdy.ai/api/search-image?query=Clean%20modern%20photography%20of%20cardboard%20shipping%20boxes%20neatly%20stacked%20with%20a%20laptop%20showing%20delivery%20tracking%20interface%2C%20warm%20cream%20background%2C%20soft%20natural%20light%2C%20sage%20green%20packaging%20tape%20accents%2C%20minimalist%20ecommerce%20logistics%20aesthetic%2C%20organized%20and%20calm%20atmosphere%2C%20high%20detail%20texture&width=1600&height=700&seq=guide-shipping-p01&orientation=landscape"
        imageAlt="Superbuy Sheet shipping and delivery guide"
        internalLinks={[
          { label: 'Complete Guide', to: '/guide', icon: 'ri-book-open-line' },
          { label: 'Safety Guide', to: '/guide/safety', icon: 'ri-shield-check-line' },
          { label: 'FAQ', to: '/guide/faq', icon: 'ri-question-answer-line' },
          { label: 'Beginner Guide', to: '/guide/beginner', icon: 'ri-compass-line' },
        ]}
      >
        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Understanding Superbuy Sheet Shipping
          </h2>
          <p>
            While <strong className="text-foreground-950">Superbuy Sheet</strong> itself serves as a
            product discovery and navigation platform, all actual purchases and shipping are handled
            through our main website at kaspreadsheet.com. This guide explains the shipping process,
            delivery expectations, and practical tips to ensure your orders arrive smoothly — regardless
            of where you are located in the world.
          </p>
          <p>
            International shipping is a key consideration for many <strong className="text-foreground-950">
            Superbuy Sheet</strong> users, particularly those shopping from the United States, United Kingdom,
            and European countries. The main platform at kaspreadsheet.com provides detailed shipping
            information for each product, including estimated delivery times, available shipping methods,
            and any region-specific considerations that may affect your order.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Shipping Process Overview
          </h2>
          <div className="space-y-5">
            {[
              { step: '1', title: 'Product Selection', desc: 'Browse categories on Superbuy Sheet and click through to your desired products on kaspreadsheet.com. Review product details including shipping availability for your region.' },
              { step: '2', title: 'Order Placement', desc: 'Complete your order on kaspreadsheet.com. The platform will display available shipping methods, estimated costs, and delivery timeframes based on your location.' },
              { step: '3', title: 'Order Processing', desc: 'After order confirmation, the seller prepares your items for shipment. Processing times vary by product type and seller, typically ranging from 1-5 business days.' },
              { step: '4', title: 'Shipping & Tracking', desc: 'Once shipped, you receive tracking information to monitor your package progress. International shipments may involve customs clearance, which can affect delivery timelines.' },
              { step: '5', title: 'Delivery', desc: 'Your order arrives at your specified shipping address. Inspect items upon receipt and contact kaspreadsheet.com support for any issues with your delivery.' },
            ].map((item) => (
              <div key={item.step} className="flex gap-4">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 font-bold text-sm">{item.step}</span>
                <div>
                  <h4 className="font-semibold text-foreground-950 mb-1">{item.title}</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Tips for International Shoppers
          </h2>
          <div className="space-y-3">
            {[
              'Check shipping availability for your country before placing an order — not all products ship to every region.',
              'Factor in customs duties and import taxes that may apply to international shipments in your country.',
              'Use the tracking information provided after shipment to monitor your package and anticipate delivery dates.',
              'Consider bundling multiple items from the same seller to potentially reduce per-item shipping costs.',
              'Be aware that international shipping times can vary significantly based on destination, shipping method, and customs processing speed.',
            ].map((tip, i) => (
              <div key={i} className="flex gap-3">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs"></i>
                </span>
                <p className="text-sm text-foreground-600 leading-relaxed">{tip}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="text-center py-6">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-base rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Visit Main Platform
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </span>
          </a>
        </section>
      </GuideLayout>
    </>
  );
}